import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Crown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { getSession } from "@/lib/track";

export default function VipSubscribeForm() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [already, setAlready] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email, first_name: firstName });
      api.post("/track/event", {
        session: getSession(),
        type: "form_submit",
        page: window.location.pathname,
        target: "vip-subscribe-form",
        customer: `${firstName ? firstName + " · " : ""}${email}`,
      }).catch(() => {});
      setAlready(!!data.already);
      setSent(true);
      toast.success(data.already ? "You're already on the VIP list." : "Welcome to the VIP list.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="vip-success-message">
        <CheckCircle2 className="w-10 h-10 text-[#0C6B4F] mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-display text-2xl md:text-3xl mb-2 text-[#0F1F1A]">
          {already ? "You're already in." : "You're on the list."}
        </h3>
        <p className="text-[#5C6B62] text-sm leading-relaxed">
          {already
            ? "This email is already subscribed — you'll be first to know when new offers drop."
            : <>Welcome aboard{firstName ? `, ${firstName}` : ""}. Watch <span className="text-[#0F1F1A]">{email}</span> for early access.</>}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="vip-subscribe-form" className="flex flex-col gap-4">
      <div className="flex items-center gap-3 mb-1">
        <Crown className="w-5 h-5 text-[#C9A227]" strokeWidth={1.75} />
        <div>
          <p className="font-display text-2xl text-[#0F1F1A] leading-none">Reserve your spot</p>
          <p className="text-xs text-[#5C6B62] mt-1.5">One email when new offers drop. Nothing else.</p>
        </div>
      </div>
      <input
        className="a-input"
        placeholder="First name (optional)"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        data-testid="vip-first-name"
      />
      <input
        className="a-input"
        type="email"
        placeholder="Email address"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="vip-email"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="vip-submit-button"
        className="bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#0A5C43] active:scale-[0.99] disabled:opacity-60 transition-[background-color,transform,opacity] duration-300"
      >
        {loading ? "Joining…" : "Join The VIP List"}
      </button>
      <p className="text-[11px] leading-relaxed text-[#8A978D] text-center" data-testid="vip-consent-text">
        By joining, you agree to our <a href="/privacy" className="underline hover:text-[#0C6B4F]">Privacy Policy</a>. Unsubscribe anytime.
      </p>
    </form>
  );
}
